<template>
  <div class="flex items-center">
    <vx-tooltip v-if="status !== NONE"
                :position="indexPosition === 0 ? 'bottom' : 'top'"
                :title="tooltipTitle"
                :text="tooltipText"
                class="flex items-center"
                :enabled="true"
    >
      <component v-if="status !== NONE"
                 :is="statusTypeIcon"
                 class="h-4 w-4"
      />
    </vx-tooltip>
  </div>
</template>

<script>
import LoadingAnimation from '@/components/common/LoadingAnimation'
import StatusSuccessIcon from '@/assets/images/icons/StatusSuccessIcon'
import StatusErrorIcon from '@/assets/images/icons/StatusErrorIcon'
import StatusWarningIcon from '@/assets/images/icons/StatusWarningIcon'
import {
  CREATED,
  DATA_PROCESSING,
  ERROR,
  FINISHED,
  getIndexExecutionStatus,
  INDEX_HALTED,
  INDEX_PROCESSING,
  INDEX_UNKNOWN,
  NONE,
  PARTIALLY_FINISHED,
  RESTARTED
} from '@/utils/index-execution.utils'

export default {
  name: 'IndexExecutionStatusIcon',

  components: {
    LoadingAnimation,
    StatusSuccessIcon,
    StatusErrorIcon,
    StatusWarningIcon
  },

  props: {
    index: {
      type: Object,
      required: true
    },
    indexPosition: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      NONE
    }
  },

  computed: {
    lastIndexExecution() {
      return this.index.last_index_execution
    },
    azureStatus() {
      return this.index.provider_status || this.index.azure_status
    },
    status() {
      return getIndexExecutionStatus(this.index)
    },
    statusTypeIcon() {
      switch (this.status) {
        case CREATED:
        case DATA_PROCESSING:
        case INDEX_PROCESSING:
        case INDEX_HALTED:
        case INDEX_UNKNOWN:
        case RESTARTED:
          return 'loading-animation'
        case FINISHED:
          return 'status-success-icon'
        case PARTIALLY_FINISHED:
          return 'status-warning-icon'
        case ERROR:
          return 'status-error-icon'
        default:
          return 'loading-animation'
      }
    },
    tooltipTitle() {
      switch (this.status) {
        case CREATED:
          return 'Queued'
        case DATA_PROCESSING:
        case INDEX_PROCESSING:
        case INDEX_UNKNOWN:
        case INDEX_HALTED: // the "paused" status was confusing people so now we pretend it's still running
          return 'Processing'
        case RESTARTED:
          return 'Reindexing from scratch'
        case PARTIALLY_FINISHED:
        case FINISHED:
          return 'Finished'
        case ERROR:
          return 'Error'
        default:
          return 'Unknown'
      }
    },
    tooltipText() {
      switch (this.status) {
        case CREATED:
          return `Indexing has been queued at position ${this.index.queue_position}.`
        case DATA_PROCESSING:
          return '1. ⏳ Downloading data<br/>2. Indexing data<br/><br/>Each step might take up to several hours depending on the dataset size.<br/>(refresh page for updated status)'
        case INDEX_PROCESSING:
        case INDEX_HALTED: // the "paused" status was confusing people so now we pretend it's still running
          const indexedCount = this.azureStatus?.itemsTotal ? `(${this.azureStatus.itemsProcessed}/${this.azureStatus.itemsTotal} indexed)` : ''
          return `1. ✅ Downloading data<br/>2. ⏳ Indexing data ${indexedCount}<br/><br/>Each step might take up to several hours depending on the dataset size.<br/>(refresh page for updated status)`
        case INDEX_UNKNOWN:
          return '1. ✅ Downloading data<br/>2. ⏳ Waiting for indexer status report.'
        case RESTARTED:
          return 'Product list/indexed items mismatch<br/><br/>1. ⏳Downloading data<br/>2. Indexing data<br/><br/>Each step might take up to several hours depending on the dataset size.<br/>(refresh page for updated status)'
        case PARTIALLY_FINISHED:
          return `
            ${this.lastIndexExecution?.error?.productCount || 0} products processed. Try to:
            <br/>- Reindex again
            <br/>- Verify your server hosting the feed is not terminating the connection
            <br/>- If you have tried above, reach Amio on support@amio.io
          `
        case FINISHED:
          return 'Indexing has completed successfully.'
        case ERROR:
          return this.lastIndexExecution.status === ERROR ?
            this.lastIndexExecution.error.message :
            this.azureStatus.errorMessage || 'Unknown error'
        default:
          return 'Unknown'
      }
    }
  }
}
</script>
